import { useCreateThread } from "@/modules/assistants/threads/threads.api";
import Button, { IconButton } from "@/lib/components/ui/button/button";
import FileInput, {
  FileInputDescription,
  FileInputTitle,
} from "@/lib/components/ui/file-input/file-input";
import Text from "@/lib/components/ui/text/text";
import Textarea from "@/lib/components/ui/textarea/textarea";
import { cn } from "@/lib/utils";
import { PROCESS_ID } from "@soulbyte/core";
import { useAuth } from "@frontegg/react";
import {
  Download,
  Play,
  RotateCcw,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import { citationTitle } from "../components/chat-message/chat-message.citations";
import {
  buildReport,
  downloadReport,
  summarizeConversations,
} from "./simulate.report";
import {
  parseSimulateDatasetText,
  type SimulateDataset,
} from "./simulate.schema";
import { useSimulate } from "./simulate.store";

/**
 * SimulatePanel is the developer tool's non-modal window: it loads conversation
 * datasets and plays each one into its own fresh thread, back to back, for
 * clean isolated results. It owns the controller that creates a thread per
 * dataset and navigates to it, which is why it mounts above the outlet and
 * survives that navigation. SimulateToggle in the chat header opens it.
 */
export default function SimulatePanel() {
  const [searchParams] = useSearchParams();
  const assistantId = searchParams.get("assistantId");
  const navigate = useNavigate();
  const { user } = useAuth();
  const createThread = useCreateThread();

  const {
    datasets,
    addDatasets,
    removeDataset,
    clearDatasets,
    open,
    setOpen,
    run,
    startRun,
    beginConversation,
    reset,
  } = useSimulate();

  const [errors, setErrors] = useState<string[]>([]);
  const [manualText, setManualText] = useState("");
  const creatingRef = useRef(false);

  // Controller: while the current dataset needs a thread, create a fresh one and
  // navigate to it, so each dataset is simulated cleanly in its own thread.
  useEffect(() => {
    if (!run || run.phase !== "need-thread") return;
    if (!assistantId || !user?.id) return;
    if (creatingRef.current) return;

    creatingRef.current = true;
    const dataset = run.datasets[run.datasetIndex];
    createThread
      .mutateAsync({
        params: { path: { assistant_id: assistantId } },
        body: {
          user_id: user.id,
          title: dataset?.name ?? "Simulation",
          stage: "draft",
          process_id: PROCESS_ID,
        },
      })
      .then((data) => {
        const thread = data?.data?.thread;
        if (!thread?.id) {
          toast.error("Could not create a thread for the simulation");
          reset();
          return;
        }
        beginConversation(thread.id, dataset?.name);
        navigate(`/chat?assistantId=${assistantId}&threadId=${thread.id}`);
      })
      .catch(() => {
        toast.error("Could not create a thread for the simulation");
        reset();
      })
      .finally(() => {
        creatingRef.current = false;
      });
    // Runs once per dataset that needs a thread; the rest is captured.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [run?.phase, run?.datasetIndex]);

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    const nextErrors: string[] = [];
    const nextDatasets: SimulateDataset[] = [];
    for (const file of Array.from(files)) {
      const result = parseSimulateDatasetText(await file.text());
      if (result.ok) {
        nextDatasets.push({
          ...result.dataset,
          name: result.dataset.name ?? file.name,
        });
      } else {
        nextErrors.push(`${file.name}: ${result.error}`);
      }
    }
    if (nextDatasets.length > 0) addDatasets(nextDatasets);
    setErrors(nextErrors);
  }

  function handleAddManual() {
    const result = parseSimulateDatasetText(manualText);
    if (!result.ok) {
      setErrors([result.error]);
      return;
    }
    addDatasets([
      { ...result.dataset, name: result.dataset.name ?? "Manual dataset" },
    ]);
    setManualText("");
    setErrors([]);
  }

  function handleDownload() {
    if (!run || !assistantId) return;
    downloadReport(buildReport(assistantId, run.conversations));
  }

  const canRun = datasets.length > 0 && Boolean(assistantId);
  const hasResults =
    run != null && run.conversations.some((c) => c.turns.length > 0);
  const summary = run ? summarizeConversations(run.conversations) : null;

  return (
    <>
      {open && (
        <div className="border-border bg-background-neutral fixed top-16 right-4 z-40 flex max-h-[70vh] w-96 max-w-[calc(100vw-2rem)] flex-col gap-3 overflow-auto rounded-2xl border p-4 shadow-lg">
          <div className="flex items-center justify-between">
            <Text weight="semibold">Simulate</Text>
            <IconButton
              variant="ghost"
              size="sm"
              onClick={() => setOpen(false)}
              aria-label="Close simulate panel"
            >
              <X className="size-4" />
            </IconButton>
          </div>

          {!assistantId && (
            <Text size="sm" className="text-foreground-pressed">
              Open a chat with an assistant to run a simulation.
            </Text>
          )}

          {run == null ? (
            <>
              <FileInput
                accept=".json,application/json"
                multiple
                onFilesChange={handleFiles}
              >
                <Upload />
                <FileInputTitle>Drop conversation JSON</FileInputTitle>
                <FileInputDescription>
                  One or more .json files
                </FileInputDescription>
              </FileInput>

              <Textarea
                value={manualText}
                onChange={(e) => setManualText(e.target.value)}
                placeholder='{ "turns": [ { "user": "Hy", "expected": "Hi" } ] }'
                rows={4}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddManual}
                disabled={manualText.trim().length === 0}
              >
                Add dataset
              </Button>

              {errors.length > 0 && (
                <div className="flex flex-col gap-1">
                  {errors.map((error) => (
                    <Text
                      key={error}
                      size="sm"
                      className="text-foreground-critical"
                    >
                      {error}
                    </Text>
                  ))}
                </div>
              )}

              {datasets.length > 0 && (
                <div className="flex flex-col gap-1">
                  {datasets.map((dataset, index) => (
                    <div
                      key={`${dataset.name}-${index}`}
                      className="flex items-center justify-between gap-2"
                    >
                      <Text size="sm" className="truncate">
                        {dataset.name ?? `Dataset ${index + 1}`} (
                        {dataset.turns.length} turns)
                      </Text>
                      <IconButton
                        variant="ghost"
                        size="sm"
                        onClick={() => removeDataset(index)}
                        aria-label="Remove dataset"
                      >
                        <Trash2 className="size-4" />
                      </IconButton>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-2">
                <Button size="sm" onClick={startRun} disabled={!canRun}>
                  <Play className="size-4" />
                  Run
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearDatasets}
                  disabled={datasets.length === 0}
                >
                  Clear all
                </Button>
              </div>
            </>
          ) : (
            <>
              <Text size="sm" className="text-foreground-pressed">
                {run.phase === "done"
                  ? "Simulation finished"
                  : run.phase === "need-thread"
                    ? `Creating thread ${run.datasetIndex + 1} of ${run.datasets.length}…`
                    : `Playing dataset ${run.datasetIndex + 1} of ${run.datasets.length}`}
              </Text>

              <div className="flex flex-col gap-3">
                {run.conversations.map((conversation) => (
                  <div
                    key={conversation.threadId}
                    className="flex flex-col gap-2"
                  >
                    <Text size="sm" weight="semibold">
                      {conversation.name ?? "Conversation"}
                    </Text>
                    {conversation.turns.map((turn, index) => (
                      <div
                        key={index}
                        className="border-border flex flex-col gap-1 rounded-lg border p-2"
                      >
                        <Text size="sm" weight="semibold">
                          {turn.user}
                        </Text>
                        {turn.expected != null && (
                          <Text size="sm" className="text-foreground-pressed">
                            expected: {turn.expected}
                          </Text>
                        )}
                        <Text
                          size="sm"
                          className={cn(
                            turn.errored && "text-foreground-critical",
                          )}
                        >
                          {turn.errored ? "error / no answer" : turn.actual}
                        </Text>
                        {turn.toolCalls.map((tool) => (
                          <Text
                            key={tool}
                            size="sm"
                            className="text-foreground-pressed"
                          >
                            tool: {tool}
                          </Text>
                        ))}
                        {turn.evidence.map((evidence) => (
                          <Text
                            key={evidence.id}
                            size="sm"
                            className="text-foreground-pressed truncate"
                          >
                            evidence: {citationTitle(evidence.title)}
                          </Text>
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
              </div>

              {run.phase === "done" && summary && (
                <div className="border-border flex flex-col gap-1 rounded-lg border p-2">
                  <Text size="sm" weight="semibold">
                    Summary
                  </Text>
                  <Text size="sm" className="text-foreground-pressed">
                    total tokens: {summary.totalTokens}
                  </Text>
                  <Text size="sm" className="text-foreground-pressed">
                    model{summary.models.length > 1 ? "s" : ""}:{" "}
                    {summary.models.join(", ") || "n/a"}
                  </Text>
                  <Text size="sm" className="text-foreground-pressed">
                    turns: {summary.totalTurns}
                    {summary.erroredTurns > 0
                      ? ` (${summary.erroredTurns} errored)`
                      : ""}
                  </Text>
                </div>
              )}

              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  onClick={handleDownload}
                  disabled={!hasResults}
                >
                  <Download className="size-4" />
                  Download report
                </Button>
                <Button variant="ghost" size="sm" onClick={reset}>
                  <RotateCcw className="size-4" />
                  New run
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
